// ============================================
// moldClaw License API Client
// ============================================

/**
 * 🧑‍💻 SWE-2: API 연동 & 에러 핸들링
 * 🔒 Sec-3: 입력 검증 & 토큰 관리
 */

import { supabase } from './supabase';

// API Base URL - 환경별 분기
const API_BASE = import.meta.env.VITE_LICENSE_API_URL || 'https://license.moldclaw.com';

// ============================================
// Types
// ============================================

export interface License {
  code: string;
  tier: 'free' | 'pro' | 'team';
  status: 'active' | 'suspended' | 'expired';
  valid_from: string;
  valid_until: string | null;
  devices_used: number;
  max_devices: number;
}

export interface Device {
  id: string;
  name: string;
  platform: string;
  last_seen_at: string;
  is_current: boolean;
}

export interface CheckoutSession {
  url: string;
}

export interface ApiError {
  code: string;
  message: string;
}

export class ApiException extends Error {
  constructor(
    public code: string,
    message: string,
    public status?: number
  ) {
    super(message);
    this.name = 'ApiException';
  }
}

// ============================================
// Token Management (라이선스 JWT)
// ============================================

let licenseAccessToken: string | null = null;
let licenseRefreshToken: string | null = null;

export function setLicenseTokens(accessToken: string, refreshToken: string) {
  licenseAccessToken = accessToken;
  licenseRefreshToken = refreshToken;
  
  // Persist to localStorage (클라이언트 앱용 - 웹에선 optional)
  try {
    localStorage.setItem('moldclaw_access_token', accessToken);
    localStorage.setItem('moldclaw_refresh_token', refreshToken);
  } catch {
    // localStorage 사용 불가 환경
  }
}

export function clearLicenseTokens() {
  licenseAccessToken = null;
  licenseRefreshToken = null;
  try {
    localStorage.removeItem('moldclaw_access_token');
    localStorage.removeItem('moldclaw_refresh_token');
  } catch {
    // ignore
  }
}

export function getLicenseTokens() {
  if (licenseAccessToken && licenseRefreshToken) {
    return { accessToken: licenseAccessToken, refreshToken: licenseRefreshToken };
  }
  
  try {
    const stored = {
      accessToken: localStorage.getItem('moldclaw_access_token'),
      refreshToken: localStorage.getItem('moldclaw_refresh_token'),
    };
    if (stored.accessToken && stored.refreshToken) {
      licenseAccessToken = stored.accessToken;
      licenseRefreshToken = stored.refreshToken;
      return stored;
    }
  } catch {
    // ignore
  }
  
  return null;
}

// ============================================
// Core API Functions
// ============================================

async function fetchApi<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  const url = `${API_BASE}/api/v1${endpoint}`;
  
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...options.headers as Record<string, string>,
  };
  
  // 인증 토큰 추가
  const tokens = getLicenseTokens();
  if (tokens?.accessToken) {
    headers['Authorization'] = `Bearer ${tokens.accessToken}`;
  }
  
  const response = await fetch(url, {
    ...options,
    headers,
  });
  
  const data = await response.json();
  
  if (!response.ok || data.success === false) {
    const error = data.error || { code: 'UNKNOWN_ERROR', message: 'Unknown error occurred' };
    
    // 토큰 만료 시 자동 갱신 시도
    if (response.status === 401 && tokens?.refreshToken && endpoint !== '/license/refresh') {
      const refreshed = await refreshLicenseToken();
      if (refreshed) {
        // 원래 요청 재시도
        return fetchApi<T>(endpoint, options);
      }
    }
    
    throw new ApiException(error.code, error.message, response.status);
  }
  
  return data as T;
}

// ============================================
// License API
// ============================================

/**
 * 라이선스 활성화 (데스크톱 앱용)
 */
export async function activateLicense(
  licenseCode: string,
  deviceFingerprint: string,
  deviceName?: string,
  platform?: string
) {
  const result = await fetchApi<{
    success: true;
    access_token: string;
    refresh_token: string;
    license: License;
    device: { id: string; name: string };
  }>('/license/activate', {
    method: 'POST',
    body: JSON.stringify({
      license_code: licenseCode,
      device_fingerprint: deviceFingerprint,
      device_name: deviceName,
      platform: platform,
    }),
  });
  
  setLicenseTokens(result.access_token, result.refresh_token);
  
  return {
    license: result.license,
    device: result.device,
  };
}

/**
 * 라이선스 토큰 갱신
 */
export async function refreshLicenseToken(): Promise<boolean> {
  const tokens = getLicenseTokens();
  if (!tokens?.refreshToken) return false;
  
  try {
    const result = await fetchApi<{
      success: true;
      access_token: string;
      refresh_token: string;
    }>('/license/refresh', {
      method: 'POST',
      body: JSON.stringify({ refresh_token: tokens.refreshToken }),
    });
    
    setLicenseTokens(result.access_token, result.refresh_token);
    return true;
  } catch {
    clearLicenseTokens();
    return false;
  }
}

/**
 * 라이선스 상태 조회
 */
export async function getLicenseStatus(): Promise<License> {
  const result = await fetchApi<{ success: true; license: License }>('/license/status');
  return result.license;
}

/**
 * 등록된 기기 목록 조회
 */
export async function getDevices(): Promise<Device[]> {
  const result = await fetchApi<{ success: true; devices: Device[] }>('/license/devices');
  return result.devices;
}

/**
 * 기기 제거
 */
export async function removeDevice(deviceId: string): Promise<void> {
  await fetchApi(`/license/devices/${deviceId}`, { method: 'DELETE' });
}

// ============================================
// Stripe / Checkout API
// ============================================

/**
 * Stripe Checkout 세션 생성
 */
export async function createCheckoutSession(
  tier: 'pro' | 'team',
  email?: string
): Promise<CheckoutSession> {
  // Supabase 세션에서 이메일 가져오기 (로그인 상태면)
  let userEmail = email;
  if (!userEmail) {
    const { data: { session } } = await supabase.auth.getSession();
    userEmail = session?.user?.email;
  }
  
  const result = await fetchApi<{
    success: true;
    checkout_url: string;
  }>('/checkout/create-session', {
    method: 'POST',
    body: JSON.stringify({
      tier,
      email: userEmail,
      success_url: `${window.location.origin}/checkout/success`,
      cancel_url: `${window.location.origin}/pricing`,
    }),
  });
  
  return { url: result.checkout_url };
}

/**
 * Stripe Customer Portal 세션 생성 (구독 관리)
 */
export async function createPortalSession(): Promise<{ url: string }> {
  // Supabase 토큰으로 인증
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) {
    throw new ApiException('UNAUTHORIZED', '로그인이 필요합니다');
  }
  
  const result = await fetchApi<{
    success: true;
    portal_url: string;
  }>('/checkout/portal-session', {
    method: 'POST',
    headers: {
      'X-Supabase-Token': session.access_token,
    },
    body: JSON.stringify({
      return_url: `${window.location.origin}/dashboard`,
    }),
  });
  
  return { url: result.portal_url };
}

// ============================================
// Sync API (Supabase 사용자 ↔ 라이선스 연동)
// ============================================

/**
 * 사용자 라이선스 정보 조회 (Supabase 인증 기반)
 */
export async function getUserLicenses(): Promise<License[]> {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) {
    throw new ApiException('UNAUTHORIZED', '로그인이 필요합니다');
  }
  
  const result = await fetchApi<{
    success: true;
    licenses: License[];
  }>('/sync/user-licenses', {
    method: 'GET',
    headers: {
      'X-Supabase-Token': session.access_token,
    },
  });
  
  return result.licenses;
}
